// ============================================================================
// CANCUN100 — AdminsTab.js  (Bloque 21: subpestaña "Administración" del panel 👥 Usuarios · solo super_admin)
// ----------------------------------------------------------------------------
// Tarjetas de `admins`: nombre, correo, rol (chip + selector), Acceso (Usuario/contraseña · Invitado · Con contraseña).
//  · "+ Invitar usuario": nombre + correo → la Edge Function invitar-agente (tipo 'admin', nuevo) crea la fila en
//    LIMBO (rol 'pendiente', contraseña inservible) y manda la invitación. El rol se asigna después.
//  · Selector de rol → update admins { rol, rol_asignado_por, rol_asignado_en }. Marco no puede quitarse super_admin.
//  · ⏸ Pausar / ▶ Reactivar → activo=false/true + pausado_por/en/motivo. NUNCA .delete().
//  · Las credenciales de admin para la Edge Function se guardan solo en memoria (igual que en AgentesTTTab).
// ============================================================================
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from './supabaseClient';
import { RADIO, RADIO_TARJETA, RADIO_INSIGNIA } from './tema';
import { COLOR_EQUIPO } from './temaEquipo'; // APP 2/20 Fase A: turquesa TT desde el tema, no suelta
import CampoSecreto from './CampoSecreto'; // Hotfix 15-oct: ojito
import useBorrador, { claveBorrador, BandaBorrador } from './useBorrador'; // Bloque 27
const INICIAL_ALTA_ADMIN = { nombre: '', correo: '' };
const esVacioAltaAdmin = (e) => !e || (!String(e.nombre || '').trim() && !String(e.correo || '').trim());

const COLS = 'id,nombre,usuario,email,rol,activo,auth_uid,invitacion_enviada_en,invitacion_por,contrasena_creada_en,rol_asignado_por,rol_asignado_en,pausado_por,pausado_en,pausado_motivo';
export const ROLES_ADMIN = [
  ['pendiente', 'Pendiente (limbo)'], ['super_admin', 'Super admin'], ['top_tours', 'Top Tours'],
  ['contabilidad', 'Contabilidad'], ['administracion', 'Administración'], ['supervisor', 'Supervisor'],
];
const ROL_LABEL = Object.fromEntries(ROLES_ADMIN);
const MSG_CORREO_DUP = 'Ese correo ya está registrado.';
const esCorreoDup = (e) => !!e && (e.code === '23505' || /CORREO_DUPLICADO/i.test(String(e.message || '')));
const norm = (s) => String(s || '').trim().toLowerCase();
const correoValido = (c) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(c || '').trim());
const fmtFecha = (f) => {
  if (!f) return '—';
  try { return new Date(f).toLocaleString('es-MX', { timeZone: 'America/Cancun', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return String(f); }
};

// Modal a nivel de módulo (si viviera en el render, los inputs perderían el foco).
const Modal = ({ C, busy, onClose, titulo, children, ancho = 460 }) => (
  <div onClick={() => !busy && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
    <div onClick={(e) => e.stopPropagation()} className="au-glass" style={{ background: C.card || C.surface, width: '100%', maxWidth: ancho, maxHeight: '88vh', overflowY: 'auto', padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{titulo}</div>
        <button type="button" onClick={onClose} disabled={busy} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 22, cursor: 'pointer' }}>✕</button>
      </div>
      {children}
    </div>
  </div>
);

export default function AdminsTab({ C: Cbase, loggedAgent = null }) {
  const C = Cbase; // APP 2/20 Fase A: C.red viene del tema
  const quien = loggedAgent?.nombre || loggedAgent?.usuario || 'admin';
  const [modal, setModal] = useState(null);
  // Bloque 27: borrador del alta de admin (nombre y correo; nunca contraseñas).
  const estadoAltaAdmin = modal && modal.tipo === 'invitar' ? { nombre: modal.nombre || '', correo: modal.correo || '' } : INICIAL_ALTA_ADMIN;
  const ponerAltaAdmin = useCallback((d) => setModal((m) => (m && m.tipo === 'invitar' ? { ...m, ...INICIAL_ALTA_ADMIN, ...(d || {}), err: '' } : m)), []);
  const borradorAltaAdmin = useBorrador(claveBorrador('alta-admin', loggedAgent?.id), estadoAltaAdmin, ponerAltaAdmin, { activo: !!modal && modal.tipo === 'invitar', inicial: INICIAL_ALTA_ADMIN, esVacio: esVacioAltaAdmin });
  const [lista, setLista] = useState([]);
  const [correosAgentes, setCorreosAgentes] = useState([]); // [{nombre, email}] para validar duplicados antes de invitar
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);
  const [verPausados, setVerPausados] = useState(false);
  const credAdmin = useRef(null);
  const showToast = (tipo, msg) => { setToast({ tipo, msg }); setTimeout(() => setToast(null), 3800); };

  const load = useCallback(async () => {
    const [a, g] = await Promise.all([
      supabase.from('admins').select(COLS).order('activo', { ascending: false }).order('nombre'),
      supabase.from('agentes').select('id,nombre,email_publico'),
    ]);
    if (a.error) { setError(a.error.message); setLoading(false); return; }
    setError('');
    setLista(a.data || []);
    setCorreosAgentes((g.data || []).map((x) => ({ nombre: x.nombre, email: norm(x.email_publico) })).filter((x) => x.email));
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  // ¿La tarjeta soy yo? (por id si la sesión lo trae; si no, por usuario/correo)
  const soyYo = (a) => (loggedAgent?.id != null && String(a.id) === String(loggedAgent.id))
    || (!!loggedAgent?.usuario && (norm(a.usuario) === norm(loggedAgent.usuario) || norm(a.email) === norm(loggedAgent.usuario)))
    || (!!loggedAgent?.email && norm(a.email) === norm(loggedAgent.email));

  const correoOcupado = (correo, excluirId) => {
    const c = norm(correo);
    if (!c) return null;
    const enAdmins = lista.find((a) => a.id !== excluirId && (norm(a.email) === c || norm(a.usuario) === c));
    if (enAdmins) return enAdmins.nombre;
    const enAgentes = correosAgentes.find((x) => x.email === c);
    return enAgentes ? enAgentes.nombre : null;
  };

  const actualizar = async (id, cambios, okMsg) => {
    setBusy(true);
    const { error: e } = await supabase.from('admins').update(cambios).eq('id', id);
    setBusy(false);
    if (e) { showToast('error', esCorreoDup(e) ? MSG_CORREO_DUP : `No se pudo guardar: ${e.message}`); return false; }
    showToast('ok', okMsg);
    setModal(null);
    await load();
    return true;
  };

  // ── Rol ──
  const cambiarRol = (a, rol) => {
    if (rol === a.rol) return;
    if (soyYo(a) && a.rol === 'super_admin' && rol !== 'super_admin') { showToast('error', 'No puedes quitarte a ti mismo el rol de super admin.'); return; }
    setModal({ tipo: 'rol', ag: a, rol });
  };
  const confirmarRol = (m) => actualizar(m.ag.id, { rol: m.rol, rol_asignado_por: quien, rol_asignado_en: new Date().toISOString() }, `${m.ag.nombre}: ahora ${ROL_LABEL[m.rol] || m.rol}`);

  // ── Pausar / Reactivar ──
  const guardarPausa = (m) => {
    if (!String(m.motivo || '').trim()) { setModal({ ...m, err: 'El motivo es obligatorio.' }); return; }
    actualizar(m.ag.id, { activo: false, pausado_por: quien, pausado_en: new Date().toISOString(), pausado_motivo: m.motivo.trim() }, `${m.ag.nombre} en pausa`);
  };
  const guardarReactivar = (a) => actualizar(a.id, { activo: true, pausado_por: null, pausado_en: null, pausado_motivo: null }, `${a.nombre} reactivado`);

  // ── Invitaciones (Edge Function invitar-agente, tipo 'admin') ──
  // Bloque 32 (B): con sesión por correo no se pide usuario/contraseña (la Edge Function v4 lee el Bearer).
  const pedirAdminY = async (destino) => {
    if (credAdmin.current) { enviar(destino); return; }
    let conSesion = false;
    try { const { data } = await supabase.auth.getSession(); conSesion = !!(data && data.session && data.session.access_token); } catch { conSesion = false; }
    if (conSesion) { enviar({ ...destino, porSesion: true }); return; }
    setModal({ tipo: 'admin', destino, usuario: loggedAgent?.usuario || '', password: '' });
  };
  const enviar = async (destino) => {
    // destino: { id, nombre, email } (existente) | { nuevo: { nombre, email } }
    setBusy(true);
    const cuerpo = { ...(destino.porSesion || !credAdmin.current ? {} : { admin_usuario: credAdmin.current.usuario, admin_password: credAdmin.current.password }), tipo: 'admin', ...(destino.nuevo ? { nuevo: destino.nuevo } : { id: destino.id }) };
    const { data, error: e } = await supabase.functions.invoke('invitar-agente', { body: cuerpo });
    let resp = data;
    if (e) { try { resp = await e.context.json(); } catch { resp = null; } }
    setBusy(false);
    if (!resp?.ok) {
      if (resp?.error === 'ADMIN' || resp?.error === 'ROL') credAdmin.current = null;
      if (destino.porSesion && (resp?.error === 'ADMIN' || resp?.error === 'ROL')) { setModal({ tipo: 'admin', destino: { ...destino, porSesion: false }, usuario: loggedAgent?.usuario || '', password: '' }); return; } // sesión no reconocida: pedir usuario
      const msg = resp?.error === 'CORREO_DUPLICADO' ? MSG_CORREO_DUP : (resp?.mensaje || (e && e.message) || 'No se pudo mandar la invitación.');
      setModal(destino.nuevo ? { tipo: 'invitar', nombre: destino.nuevo.nombre, correo: destino.nuevo.email, err: msg } : null);
      if (!destino.nuevo) showToast('error', msg);
      await load();
      return;
    }
    if (destino.nuevo) borradorAltaAdmin.limpiar(); // Bloque 27
    setModal(null);
    showToast('ok', `${resp.reenvio ? 'Invitación reenviada' : 'Invitación enviada'} a ${resp.email}${destino.nuevo ? ' · queda en limbo hasta que le asignes rol' : ''}`);
    await load();
  };
  const invitarNuevo = (m) => {
    const nombre = String(m.nombre || '').trim();
    const correo = String(m.correo || '').trim().toLowerCase();
    if (nombre.length < 3) { setModal({ ...m, err: 'Escribe el nombre completo.' }); return; }
    if (!correoValido(correo)) { setModal({ ...m, err: 'El correo no tiene un formato válido.' }); return; }
    const dup = correoOcupado(correo, null);
    if (dup) { setModal({ ...m, err: `${MSG_CORREO_DUP} (${dup})` }); return; }
    pedirAdminY({ nuevo: { nombre, email: correo } });
  };

  // ── Derivados ──
  const ql = norm(q);
  const coincide = (a) => !ql || [a.nombre, a.email, a.usuario, ROL_LABEL[a.rol]].some((x) => norm(x).includes(ql));
  const activos = lista.filter((a) => a.activo !== false).filter(coincide);
  const pausados = lista.filter((a) => a.activo === false).filter(coincide);
  const kpis = [
    ['Activos', lista.filter((a) => a.activo !== false).length, C.green],
    ['En limbo', lista.filter((a) => a.activo !== false && a.rol === 'pendiente').length, C.yellow],
    ['Con contraseña', lista.filter((a) => a.activo !== false && a.contrasena_creada_en).length, C.blue],
    ['Pausados', lista.filter((a) => a.activo === false).length, C.muted],
  ];
  const accesoDe = (a) => (a.contrasena_creada_en ? ['Con contraseña', fmtFecha(a.contrasena_creada_en), C.green]
    : a.invitacion_enviada_en ? ['Invitado', fmtFecha(a.invitacion_enviada_en), C.yellow]
    : ['Usuario/contraseña', '', C.muted]);
  const colorRol = (rol) => ({ super_admin: C.rosa, top_tours: COLOR_EQUIPO.tt, contabilidad: C.blue, administracion: C.naranja, supervisor: C.green }[rol] || C.yellow);

  // ── Estilos ──
  const card = { background: 'var(--au-glass-fill)', border: '1px solid var(--au-glass-border)', borderRadius: RADIO_TARJETA, backdropFilter: 'blur(var(--au-glass-blur))', WebkitBackdropFilter: 'blur(var(--au-glass-blur))', boxShadow: 'var(--au-shadow-card)', padding: 16, marginBottom: 14 };
  const input = { width: '100%', background: C.dark, border: `1px solid ${C.border}`, borderRadius: RADIO, height: 40, boxSizing: 'border-box', padding: '0 12px', color: C.text, fontSize: 14, fontFamily: 'var(--au-font-text)', outline: 'none', boxSizing: 'border-box' };
  const lbl = { fontSize: 14, color: C.muted, fontWeight: 600, marginBottom: 6, display: 'block' };
  const btn = (extra) => ({ height: 32, boxSizing: 'border-box', padding: '0 12px', fontSize: 12, ...extra });
  const ErrorBox = ({ msg }) => (msg ? <div className="au-banner au-banner--error" style={{ marginBottom: 12, fontSize: 12 }}>{msg}</div> : null);
  const set = (patch) => setModal((m) => ({ ...m, ...patch, err: '' }));

  const renderModal = () => {
    if (!modal) return null;
    const m = modal;
    if (m.tipo === 'invitar') {
      return (
        <Modal C={C} busy={busy} onClose={() => setModal(null)} titulo="✉️ Invitar usuario" ancho={480}>
          <BandaBorrador C={C} borrador={borradorAltaAdmin} compacta />
          <ErrorBox msg={m.err} />
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>Recibe un correo para crear su contraseña. Entra en <strong style={{ color: C.text }}>limbo</strong> (sin panel) hasta que le asignes un rol aquí.</div>
          <label style={lbl}>Nombre completo</label>
          <input style={{ ...input, marginBottom: 14 }} value={m.nombre || ''} onChange={(e) => set({ nombre: e.target.value })} />
          <label style={lbl}>Correo</label>
          <input type="email" style={input} value={m.correo || ''} onChange={(e) => set({ correo: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') invitarNuevo(m); }} />
          {m.correo && correoOcupado(m.correo, null) && <div style={{ fontSize: 12, color: C.red, marginTop: 6 }}>{MSG_CORREO_DUP} ({correoOcupado(m.correo, null)})</div>}
          <div className="au-pie">
            <button type="button" className="au-btn au-btn--ghost" onClick={() => setModal(null)} disabled={busy}>Cancelar</button>
            <button type="button" className="au-btn au-btn--primary" onClick={() => invitarNuevo(m)} disabled={busy}>{busy ? 'Enviando…' : '✉️ Crear y enviar invitación'}</button>
          </div>
        </Modal>
      );
    }
    if (m.tipo === 'admin') {
      const confirmar = () => {
        if (!String(m.usuario || '').trim() || !m.password) { setModal({ ...m, err: 'Escribe tu usuario y tu contraseña de administrador.' }); return; }
        credAdmin.current = { usuario: String(m.usuario).trim(), password: m.password };
        enviar(m.destino);
      };
      return (
        <Modal C={C} busy={busy} onClose={() => setModal(null)} titulo="Confirma que eres tú">
          <ErrorBox msg={m.err} />
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>Para mandar la invitación confirma tu acceso de administrador. Solo se usa en esta sesión y no se guarda.</div>
          <label style={lbl}>Usuario de administrador</label>
          <input style={{ ...input, marginBottom: 14 }} autoComplete="username" value={m.usuario || ''} onChange={(e) => set({ usuario: e.target.value })} />
          <label style={lbl}>Contraseña de administrador</label>
          <CampoSecreto C={C} autoComplete="current-password" style={input} value={m.password || ''} onChange={(v) => set({ password: v })} onKeyDown={(e) => { if (e.key === 'Enter') confirmar(); }} />
          <div className="au-pie">
            <button type="button" className="au-btn au-btn--ghost" onClick={() => setModal(null)} disabled={busy}>Cancelar</button>
            <button type="button" className="au-btn au-btn--primary" onClick={confirmar} disabled={busy}>{busy ? 'Enviando…' : '✉️ Enviar invitación'}</button>
          </div>
        </Modal>
      );
    }
    if (m.tipo === 'rol') {
      return (
        <Modal C={C} busy={busy} onClose={() => setModal(null)} titulo={`Asignar rol · ${m.ag.nombre}`}>
          <div style={{ fontSize: 14, color: C.text, marginBottom: 14 }}>
            De <strong>{ROL_LABEL[m.ag.rol] || m.ag.rol}</strong> a <strong>{ROL_LABEL[m.rol]}</strong>. {m.rol === 'pendiente' ? 'Queda en limbo: no verá ningún panel.' : 'Verá su panel la próxima vez que entre o recargue.'}
          </div>
          <div className="au-pie">
            <button type="button" className="au-btn au-btn--ghost" onClick={() => setModal(null)} disabled={busy}>Cancelar</button>
            <button type="button" className="au-btn au-btn--primary" onClick={() => confirmarRol(m)} disabled={busy}>Asignar {ROL_LABEL[m.rol]}</button>
          </div>
        </Modal>
      );
    }
    if (m.tipo === 'pausar') {
      return (
        <Modal C={C} busy={busy} onClose={() => setModal(null)} titulo={`⏸ Pausar · ${m.ag.nombre}`}>
          <ErrorBox msg={m.err} />
          <div className="au-banner au-banner--error" style={{ fontSize: 12, marginBottom: 12 }}>No podrá entrar hasta que lo reactives. No se borra nada.</div>
          <label style={lbl}>Motivo (obligatorio)</label>
          <textarea rows={3} style={{ ...input, height: 'auto', minHeight: 72, padding: '10px 12px', resize: 'vertical' }} value={m.motivo || ''} onChange={(e) => set({ motivo: e.target.value })} />
          <div className="au-pie">
            <button type="button" className="au-btn au-btn--ghost" onClick={() => setModal(null)} disabled={busy}>Cancelar</button>
            <button type="button" className="au-btn au-btn--primary" onClick={() => guardarPausa(m)} disabled={busy} style={{ background: C.red }}>⏸ Pausar</button>
          </div>
        </Modal>
      );
    }
    if (m.tipo === 'reactivar') {
      return (
        <Modal C={C} busy={busy} onClose={() => setModal(null)} titulo={`▶ Reactivar · ${m.ag.nombre}`}>
          <div style={{ fontSize: 14, color: C.text, marginBottom: 14 }}>Podrá volver a entrar con su mismo acceso y su rol ({ROL_LABEL[m.ag.rol] || m.ag.rol}).</div>
          <div className="au-pie">
            <button type="button" className="au-btn au-btn--ghost" onClick={() => setModal(null)} disabled={busy}>Cancelar</button>
            <button type="button" className="au-btn au-btn--primary" onClick={() => guardarReactivar(m.ag)} disabled={busy}>▶ Reactivar</button>
          </div>
        </Modal>
      );
    }
    return null;
  };

  const tarjeta = (a) => {
    const activo = a.activo !== false;
    const yo = soyYo(a);
    const [t, fch, col] = accesoDe(a);
    const puedeInvitar = activo && !a.contrasena_creada_en && correoValido(a.email || a.usuario);
    return (
      <div key={a.id} style={{ ...card, opacity: activo ? 1 : 0.75, borderLeft: `3px solid ${activo ? (a.rol === 'pendiente' ? C.yellow : C.green) : C.muted}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div style={{ minWidth: 220, flex: '1 1 260px' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
              {a.nombre}{yo && <span className="au-badge au-badge--neutral" style={{ fontSize: 12, marginLeft: 8 }}>tú</span>}
              <span style={{ marginLeft: 8, padding: '2px 8px', borderRadius: RADIO_INSIGNIA, fontSize:12, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: colorRol(a.rol), background: `${colorRol(a.rol)}1f`, border: `1px solid ${colorRol(a.rol)}55` }}>{ROL_LABEL[a.rol] || a.rol || '—'}</span>
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{a.email || a.usuario || 'sin correo'}{a.usuario && a.email && norm(a.usuario) !== norm(a.email) && <> · usuario: {a.usuario}</>}</div>
            <div style={{ fontSize: 12, marginTop: 4, color: C.muted }}>Acceso: <span style={{ color: col, fontWeight: 700 }}>{t}</span>{fch && <> · {fch}</>}{a.invitacion_por && !a.contrasena_creada_en && <> · por {a.invitacion_por}</>}</div>
            {a.rol_asignado_en && <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>Rol asignado {fmtFecha(a.rol_asignado_en)}{a.rol_asignado_por && <> · por {a.rol_asignado_por}</>}</div>}
            <div style={{ marginTop: 8 }}>
              {activo
                ? <span className="au-badge au-badge--success" style={{ fontSize: 12 }}>Activo</span>
                : (
                  <div style={{ fontSize: 12, color: C.red }}>
                    <span className="au-badge au-badge--error" style={{ fontSize: 12, marginRight: 6 }}>⏸ En pausa</span>
                    {fmtFecha(a.pausado_en)}{a.pausado_por && <> · por {a.pausado_por}</>}
                    {a.pausado_motivo && <div style={{ color: C.muted, marginTop: 3 }}>Motivo: {a.pausado_motivo}</div>}
                  </div>
                )}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: C.muted }}>
              Rol
              <select value={a.rol || 'pendiente'} disabled={busy || !activo} onChange={(e) => cambiarRol(a, e.target.value)}
                className="au-input" style={{ width: 'auto', padding: '6px 10px', fontSize: 12, borderRadius: RADIO }}>
                {ROLES_ADMIN.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {puedeInvitar && (
                <button type="button" className="au-btn au-btn--secondary" style={btn()} disabled={busy} onClick={() => pedirAdminY({ id: a.id, nombre: a.nombre, email: a.email || a.usuario })}>
                  ✉️ {a.invitacion_enviada_en ? 'Reenviar invitación' : 'Enviar invitación'}
                </button>
              )}
              {activo
                ? (!yo && <button type="button" className="au-btn au-btn--ghost" style={btn({ color: C.red })} disabled={busy} onClick={() => setModal({ tipo: 'pausar', ag: a })}>⏸ Pausar</button>)
                : <button type="button" className="au-btn au-btn--secondary" style={btn({ color: C.green })} disabled={busy} onClick={() => setModal({ tipo: 'reactivar', ag: a })}>▶ Reactivar</button>}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: C.muted }}>Cargando administradores…</div>;

  return (
    <div style={{ marginTop: 20 }}>
      {toast && (
        <div className={`au-banner ${toast.tipo === 'error' ? 'au-banner--error' : 'au-banner--success'}`} style={{ position: 'fixed', top: 80, left: '50%', transform: 'translateX(-50%)', zIndex: 600, fontWeight: 600, boxShadow:'var(--au-shadow-card)' }}>{toast.msg}</div>
      )}
      {renderModal()}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--au-text-primary)', fontFamily: 'var(--au-font-text)', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 3, height: 16, background: 'var(--au-raya)', borderRadius: 2, display: 'inline-block' }} />
          👥 Agentes · Admins
        </h2>
        <button type="button" className="au-btn au-btn--primary" onClick={() => setModal({ tipo: 'invitar', nombre: '', correo: '' })}>✉️ Invitar usuario</button>
      </div>
      {error && <div className="au-banner au-banner--error" style={{ marginBottom: 12 }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))', gap: 10, marginBottom: 14 }}>
        {kpis.map(([l, v, col]) => (
          <div key={l} style={{ ...card, marginBottom: 0, padding: '16px 14px' }}>
            <div style={{ fontSize: 12, color: 'var(--au-text-faint)', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 4 }}>{l}</div>
            <div style={{ fontSize: 30, fontWeight: 700, color: col, fontFamily: 'var(--au-font-text)' }}>{v}</div>
          </div>
        ))}
      </div>

      <div style={{ ...card, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', padding: '12px 16px' }}>
        <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, correo o rol..." style={{ ...input, paddingLeft: 34 }} />
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 14, opacity: 0.4 }}>🔍</span>
        </div>
        <div style={{ fontSize: 12, color: C.muted }}>{activos.length} activo{activos.length !== 1 ? 's' : ''}{pausados.length > 0 && <> · {pausados.length} en pausa</>}</div>
      </div>

      {activos.length === 0 && <div style={{ ...card, textAlign: 'center', color: C.muted, padding: 40 }}>{q ? 'Sin usuarios que coincidan.' : 'Sin administradores activos.'}</div>}
      {activos.map(tarjeta)}

      {pausados.length > 0 && (
        <div style={{ ...card, padding: '10px 16px' }}>
          <button type="button" onClick={() => setVerPausados((v) => !v)} aria-expanded={verPausados}
            style={{ width: '100%', background: 'none', border: 'none', color: C.text, fontSize: 14, fontWeight: 700, cursor: 'pointer', textAlign: 'left', padding: '6px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>⏸ Pausados ({pausados.length})</span><span style={{ color: C.muted, fontSize: 12 }}>{verPausados ? 'Ocultar ▲' : 'Ver ▼'}</span>
          </button>
          {verPausados && <div style={{ marginTop: 10 }}>{pausados.map(tarjeta)}</div>}
        </div>
      )}
    </div>
  );
}
